import { useState, useMemo } from 'react'
import { isAfter, isEqual, isValid } from 'date-fns'

import { Button } from '@wartek-id/button-v1'
import { Card } from '@wartek-id/card'
import { Text } from '@wartek-id/text-v1'
import { Shimmer } from '@wartek-id/shimmer'
import {
  Dialog,
  DialogOverlay,
  DialogContent,
  DialogHeader,
} from '@wartek-id/dialog-v1'

import { assetUrl } from 'utils/url'
import { useAPIGetPelaksanaanConfig } from 'api/kinerja/useAPIKinerja'
import { usePelaksanaanDate } from 'app/KinerjaShared/hooks/usePelaksanaanDate'

import type { ReactNode } from 'react'

const DEFAULT_PENILAIAN_STEPS = [
  {
    title: 'Penilaian Kinerja',
    description:
      'Penilaian Praktik Kinerja, Pengembangan Kompetensi, dan Perilaku Kerja.',
  },
  {
    title: 'Pengiriman data Penilaian Kinerja ke Dinas Pendidikan',
    description:
      'Dilakukan setelah seluruh Pegawai selesai dinilai. Pengiriman bukan tahap akhir dan dilakukan hanya agar Predikat Kinerja Organisasi dapat diproses.',
  },
  {
    title: 'Predikat Kinerja Organisasi sedang diproses',
    description:
      'Predikat akan ditetapkan berdasarkan rekomendasi dari Kemendikbudristek bersama Dinas Pendidikan.',
  },
  {
    title: 'Penetapan Predikat Kinerja Pegawai',
    description: (
      <>
        Ini adalah <strong>tahap akhir</strong> yang dilakukan untuk Penilaian
        Kinerja. Penetapan dilakukan berdasarkan Predikat Kinerja Organisasi.
      </>
    ),
  },
  {
    title: 'Penerimaan Dokumen Evaluasi Kinerja',
    description:
      'Dokumen Evaluasi Kinerja Pegawai untuk periode yang sedang berlangsung akan diterima oleh masing-masing Pegawai.',
  },
]

export type TahapPenilaianCardProps = {
  steps?: Array<{
    title: string
    description: ReactNode
  }>
}

export function TahapPenilaianCard({
  steps = DEFAULT_PENILAIAN_STEPS,
}: TahapPenilaianCardProps) {
  // -----------------------------------------
  // Handle detail dialog
  // -----------------------------------------
  const [isOpen, setOpen] = useState(false)

  // -----------------------------------------
  // Resolve visibility flag
  // -----------------------------------------
  const { isStarted: isPelaksanaanStarted } = usePelaksanaanDate()

  const { data: pelaksanaanConfigData, isLoading: isPelaksanaanConfigLoading } =
    useAPIGetPelaksanaanConfig({
      params: { key: 'PENETAPAN_PREDIKAT_KINERJA' },
      options: {
        staleTime: 60 * 60 * 1000, // 1 hour
        // Disable error boundary since the data is not required for this page
        useErrorBoundary: false,
      },
    })

  const showTahapPenilaian = useMemo(() => {
    const accessDate =
      pelaksanaanConfigData?.data?.organizationProgressAccessDate
    const isValidAccessDate = isValid(new Date(accessDate))
    const currentDate = new Date()
    const accessDateObject = new Date(accessDate)
    const isPenilaianStarted =
      isValidAccessDate &&
      (isAfter(currentDate, accessDateObject) ||
        isEqual(currentDate, accessDateObject))

    return isPelaksanaanStarted && isPenilaianStarted
  }, [pelaksanaanConfigData, isPelaksanaanStarted])

  if (isPelaksanaanConfigLoading) return <Shimmer className="!w-full !h-96" />

  if (!showTahapPenilaian) return null

  return (
    <>
      <Card className="py-4 px-6 !flex-row space-x-6 items-start">
        <img
          src={assetUrl('/illustrations/img-flow-process-document.png')}
          alt=""
          width={84}
          height={84}
          className="flex-none"
        />
        <div className="w-full max-w-lg flex-1">
          <Text variant="heading-md" className="!text-black mb-1" as="h2">
            Mari cek rangkaian tahap Penilaian Kinerja!
          </Text>
          <Text color="subdued" className="mb-4">
            Diskusikan tahap-tahap ini dengan guru jika diperlukan.
          </Text>
          <Button
            size="sm"
            aria-label="Lihat tahap selengkapnya"
            onClick={() => setOpen(true)}
          >
            Selengkapnya
          </Button>
        </div>
      </Card>

      <Dialog isOpen={isOpen} size="md">
        <DialogOverlay />
        <DialogContent>
          <DialogHeader
            title="Rangkaian tahap Penilaian Kinerja"
            borderless={true}
          />
          <div className="p-6">
            <ol className="mb-2">
              {steps.map((step, i) => {
                return (
                  <li key={i} className="flex space-x-4">
                    <div
                      className="w-6 h-6 rounded-full bg-gray-80 flex-none flex items-center justify-center"
                      role="presentation"
                    >
                      <Text variant="body-sm" color="inverse">
                        {i + 1}
                      </Text>
                    </div>
                    <div className="mb-4">
                      <Text as="h3" variant="action" className="mb-1">
                        {step.title}
                      </Text>
                      <Text color="subdued">{step.description}</Text>
                    </div>
                  </li>
                )
              })}
            </ol>
            <Button
              size="lg"
              className="mx-auto"
              onClick={() => setOpen(false)}
            >
              Saya mengerti
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  )
}
